import { useDragDropManager, useDroppable } from '@dnd-kit/react';
import { Tooltip, UnstyledButton } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import clsx from 'clsx';
import { useCallback } from 'react';
import { useLiveStateManager } from '~/hooks/useLiveStateManager';
import { api } from '~/trpc-extra/api';
import { AddOperationPopover } from './AddOperationPopover';
import { TOUR_ID } from '~/components/tours/DraftEditorTour';
import type { TestStepMode } from '@prisma/client';
import { useHover } from '@mantine/hooks';
import type { OperationParentContext } from '~/server/logic/steps/operation/types';
import { componentModificationWarningAtom } from '~/atoms/no-code-atoms';
import { useSetAtom } from 'jotai';

/**
 * New operation control always add an operation to BEFORE the operation with the operationId.
 * If only stepId is provided, the new operation will be inserted as the last operation in the step.
 * If componentId is provided, the new operation will be inserted into the component instead of the step.
 */
export default function NewOperationControl({
  testContentId,
  // The step that the new operation will be inserted into.
  stepId,
  stepOrder,
  stepMode,
  parentContexts,
  insertAfterOperationOrder,
  operationId,
  prevOperationId,
  // The operation that will be after the inserted operation. Used for drag and drop.
  nextOperationId,
  activePageAlias,
  readonly,
  // The order of the operation that will be after the inserted operation. Used for inserting new operation before this one.
  // Needed for updating the operation's step's description after an operation is added
}: {
  testContentId: string;
  // The stepId is always required for:
  // - Recorder cursor positioning
  // - UI state management and invalidation
  // - Context on where the component operation is in the step
  stepId: string;
  // The parent context of this new operation to add (we may be in a component or conditional block)
  parentContexts?: OperationParentContext[];
  stepOrder: number;
  stepMode: TestStepMode;
  insertAfterOperationOrder: number;
  operationId?: string;
  prevOperationId?: string;
  nextOperationId?: string;
  activePageAlias?: string;
  readonly?: boolean;
}) {
  const parentContext = parentContexts?.at(-1);
  const setComponentModificationWarning = useSetAtom(componentModificationWarningAtom);

  const { mutateAsync: addStep } = api.testStep.insert.useMutation({
    onSuccess: () => {
      notifications.show({ message: 'Step created', color: 'blue' });
    },
    onError: () => {
      notifications.show({ message: 'Error creating step', color: 'red' });
    },
  });

  const { hovered, ref: hoverRef } = useHover();

  const manager = useDragDropManager();
  const draggedSourceId = manager?.dragOperation.source?.data.operationId;
  const draggedSourceType = manager?.dragOperation.source?.data.type;

  const idParentContext =
    parentContext?.componentId ? `component:${parentContext.componentId}`
    : parentContext?.conditional ?
      `conditional:${parentContext.conditional.conditionalBlockId}:${parentContext.conditional.blockType}`
    : stepId;

  const droppableId = `${idParentContext}:${nextOperationId ? `before:${nextOperationId}` : 'end'}`;

  const { isDropTarget, ref: droppableRef } = useDroppable({
    id: droppableId,
    data: {
      type: 'operation',
      targetNextOperationId: nextOperationId,
      targetStepId: stepId,
      targetParentContext: parentContext,
    },
    // Disallow an operation to be dropped on itself
    disabled:
      (nextOperationId !== undefined && draggedSourceId === nextOperationId) || draggedSourceType !== 'operation',
  });

  const { vmStatus, recorderState, updateLiveState } = useLiveStateManager();

  const isSameParentContext = recorderState?.parentContext?.rootOperationId === parentContext?.rootOperationId;

  const isRecorderInSameContext =
    ((parentContext?.componentId && recorderState?.parentContext?.componentId === parentContext.componentId) ||
      (!parentContext?.componentId &&
        !recorderState?.parentContext?.componentId &&
        recorderState?.stepId === stepId)) &&
    ((parentContext?.conditional?.conditionalBlockId &&
      recorderState?.parentContext?.conditional?.conditionalBlockId === parentContext.conditional.conditionalBlockId &&
      recorderState?.parentContext?.conditional?.blockType === parentContext.conditional.blockType) ||
      (!parentContext?.conditional?.conditionalBlockId &&
        !recorderState?.parentContext?.conditional?.conditionalBlockId &&
        recorderState?.stepId === stepId));

  const isActiveRecordingPosition =
    // we're on the active operation
    recorderState?.operation?.id === prevOperationId &&
    // and we're in the same parent op (component or conditional)
    isRecorderInSameContext &&
    isSameParentContext;

  const isJoyrideOperation = operationId === TOUR_ID.MOVE_CURSOR;

  const tryUpdateLiveState = useCallback(
    async (newState: {
      parentContext?: OperationParentContext;
      stepId: string;
      stepOrder: number;
      operation?: { id: string; order: number };
    }) => {
      try {
        updateLiveState((prev) => ({
          ...prev,
          recorderCursor: newState,
        }));
      } catch (e) {
        console.debug('Error moving recorder cursor:', e);
      }
    },
    [updateLiveState],
  );

  const moveRecorderCursor = useCallback(async () => {
    if (stepMode === 'AI_DYNAMIC') {
      const newStep = await addStep({
        description: '',
        order: stepOrder + 1,
        testContentId,
        mode: 'AI_FALLBACK',
      });
      await tryUpdateLiveState({ stepId: newStep.id, stepOrder: newStep.stepOrder });
    } else {
      const parentComponentIds =
        parentContexts?.map(({ componentId }) => componentId).filter((id) => id !== undefined) ?? [];

      const updateCursor = async () =>
        tryUpdateLiveState({
          parentContext,
          stepId,
          stepOrder,
          operation:
            prevOperationId ?
              {
                id: prevOperationId,
                order: insertAfterOperationOrder,
              }
            : undefined,
        });

      // No need to show warning if we're jumping the cursor to the same component
      if (parentComponentIds.length > 0 && recorderState?.parentContext?.componentId !== parentComponentIds.at(-1)) {
        setComponentModificationWarning({
          componentId: parentComponentIds.at(-1)!,
          rootOperationId: parentContexts?.at(-1)?.rootOperationId,
          currentTestContentId: testContentId,
          onConfirm: updateCursor,
        });
      } else {
        await updateCursor();
      }
    }
  }, [
    testContentId,
    parentContext,
    stepId,
    stepOrder,
    stepMode,
    prevOperationId,
    insertAfterOperationOrder,
    addStep,
    tryUpdateLiveState,
    parentContexts,
  ]);

  const cursorAllowed =
    recorderState?.executionType === 'record' ||
    ((stepMode !== 'AI_DYNAMIC' || !nextOperationId) && !readonly && vmStatus?.state === 'ready') ||
    isJoyrideOperation;

  return (
    <div ref={hoverRef}>
      <div className="flex w-full flex-col">
        {stepMode !== 'AI_DYNAMIC' &&
          !!cursorAllowed &&
          recorderState?.operation?.id === undefined &&
          !!isActiveRecordingPosition &&
          !nextOperationId && (
            <div className="justify-self-start pb-2 pl-[42px] text-sm text-gray-600 dark:text-gray-400">
              Waiting for user action...
            </div>
          )}

        <div
          ref={droppableRef}
          className="relative flex h-2 w-full justify-center"
        >
          {!!cursorAllowed && (
            <div className="absolute -left-2 top-1/2 z-[50] flex -translate-x-4 -translate-y-1/2 items-center gap-1">
              <Tooltip
                label={
                  isActiveRecordingPosition ? 'Recorded actions will be added below' : 'Click to move recorder cursor'
                }
                multiline
                withArrow
                className="text-center"
                w={200}
                openDelay={500}
                position="top"
              >
                <UnstyledButton
                  className={clsx(
                    'ml-1 px-4 py-1',
                    isActiveRecordingPosition ? 'opacity-100'
                    : isJoyrideOperation ? 'opacity-50'
                    : 'opacity-0 hover:opacity-50',
                  )}
                  onClick={moveRecorderCursor}
                >
                  <div
                    id={isActiveRecordingPosition ? TOUR_ID.RECORDER_CURSOR : ''}
                    className={clsx('h-4 w-1 rounded-sm bg-red-500', {
                      'animate-[pulse_0.75s_ease-in-out_infinite]': isActiveRecordingPosition,
                    })}
                  />
                </UnstyledButton>
              </Tooltip>
            </div>
          )}

          {!readonly && isDropTarget ?
            <hr className="mx-auto w-[90%] border-dashed border-blue-400" />
          : null}

          {!readonly && (
            <div
              className={clsx(
                'delay-50 absolute z-[49] flex w-full -translate-y-1/3 justify-center transition-[visibility] duration-0',
                hovered ? 'visible' : 'invisible',
              )}
            >
              <AddOperationPopover
                insertAfterOperationOrder={insertAfterOperationOrder}
                activePageAlias={activePageAlias}
                parentStepOrder={stepOrder}
                parentStepId={stepId}
                operationId={operationId}
                testContentId={testContentId}
                parentContexts={parentContexts}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
